import React, {
  useState,
  useRef,
  useEffect,
  ReactElement,
  useContext,
} from "react";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import List from "@mui/material/List";
import CircularProgress from "@mui/material/CircularProgress";

import { useBottomScrollListener } from "react-bottom-scroll-listener";
import { registrationStatus } from "../../dataProvider/interfaces/IAttendee";
import GenericListItem from "../GenericListItem/GenericListItem";
import { theme } from "../../theme";
import { BiTrash } from "react-icons/bi";
import { SimpleActionDialog } from "..";
import { actionDialogType } from "../SimpleActionDialog/SimpleActionDialog";
import { FiLogOut } from "react-icons/fi";
import FdButton from "../UIComponents/Button/FdButton";
import { genericList as classes } from "./styles";
import { StoreContext } from "../../store/Store";

export interface Props {
  type: dataType;
  items: any[];
  onSelectItem: Function;
  onBottomScroll?: Function; // a bottom scroll callback must be provided if there is API pagination
  onDeleteItem?: Function; // delete items callback
  selectedItem?: any;
  style?: React.CSSProperties;
  onEditItem?: Function;
  onDuplicateItem?: Function;
  setDefault?: Function;
  isLoading?: boolean;
  bulkActions?: any;
  customActions?: any;
  showFullSession?: boolean;
  dispatch?: Function;
  reservedSessions?: any[];
  currentReservations?: any[];
  setReservationUpdate?: any;
  setSelectAllValue?: any;
  className?: string;
  selectAllValue?: boolean;
}

export interface IReservationDetail {
  sessionId: number;
  sessionAmount: string;
}

export enum dataType {
  USERS = "USERS",
  ATTENDEES = "ATTENDEES",
  SESSIONS = "SESSIONS",
  RESERVATIONS = "RESERVATIONS",
  ACCOUNTS = "ACCOUNTS",
  BADGE_FORMATS = "BADGE_FORMATS",
}

/** Generic list component */

const GenericList = ({
  items,
  onSelectItem,
  type,
  onBottomScroll,
  onDeleteItem,
  onEditItem,
  onDuplicateItem,
  setDefault,
  selectedItem,
  style,
  isLoading,
  bulkActions, // actions shown on top left
  customActions, // actions shown on top right
  className,
}: Props) => {
  const [loadedItems, setLoadedItems] = useState(
    onBottomScroll ? items : items.slice(0, 50) // initialize the state with all the provided items if API pagination is implemented
  );
  const [checkedItems, setCheckedItems] = useState<number[]>([]);
  const [actionDialog, setActionDialog] = useState<any>({
    open: false,
    title: "",
    icon: <BiTrash size="45" />,
    type: actionDialogType.WARNING,
    onCancel: () => {},
    onClose: () => {},
    onConfirm: () => {},
  });
  const [deleteReserves, setDeleteReserves] = useState<IReservationDetail[]>(
    []
  );
  useEffect(() => {
    // update state only when new items are loaded from the API, new items are loaded on bottom scroll
    if (onBottomScroll) {
      setLoadedItems(items);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  // build the items list progressively (load only 10 in a row) to avoid the slow rendering of DOM elements
  let scrollRef = useRef<any>();
  scrollRef = useBottomScrollListener(handleBottomScrollEvent, {
    triggerOnNoScroll: false,
    debounce: 200,
  });

  function handleBottomScrollEvent() {
    // load more elements if there is API pagination
    if (onBottomScroll) {
      onBottomScroll();
    }

    // load DOM elements progressively if there is no API pagination or when importing local large CSV files
    if (!onBottomScroll && loadedItems.length < items.length) {
      setLoadedItems([
        ...loadedItems,
        ...items.slice(loadedItems.length, loadedItems.length + 50),
      ]);
    }
  }

  const hideWarningDialog = () => {
    const newActionDialog = { ...actionDialog, open: false };
    setActionDialog(newActionDialog);
  };

  const showWarningDialog = (
    message: string,
    confirmCallback: Function,
    icon?: ReactElement
  ) => {
    const newActionDialog = {
      ...actionDialog,
      open: true,
      ...(icon && { icon: icon }),
      onCancel: () => {
        hideWarningDialog();
      },
      onClose: () => {
        hideWarningDialog();
      },
      onConfirm: () => {
        hideWarningDialog();
        confirmCallback();
      },
      title: message,
    };
    setActionDialog(newActionDialog);
  };

  const handleItemSelect = (item: any) => {
    onSelectItem(item);
  };

  const handleEditItem = (item: any) => {
    onEditItem && onEditItem(item);
  };

  const handleDuplicateItem = (item: any) => {
    onDuplicateItem && onDuplicateItem(item);
  };

  const handleSetDefault = (item: any) => {
    setDefault && setDefault(item);
  };
  const handleItemCheck = (id: number, amount?: string) => {
    const currentCheckedItems = [...checkedItems];
    const currentIndex = currentCheckedItems.indexOf(id);
    const newChecked = [...currentCheckedItems];
    if (currentIndex === -1) {
      newChecked.push(id);
    } else {
      newChecked.splice(currentIndex, 1);
    }
    setCheckedItems(newChecked);
  };

  const handleDeleteItem = (item: any) => {
    if (onDeleteItem) {
      showWarningDialog(
        type === dataType.RESERVATIONS
          ? `Are you sure you want to delete the reservation for ${item.attendeeName}`
          : `Are you sure you want to delete ${
              item.name || item.fullName || "the selected item"
            }?`,
        () => {
          hideWarningDialog();
          if (type === dataType.RESERVATIONS) {
            onDeleteItem(item.attendeeId);
          } else {
            onDeleteItem(item.id);
          }
        }
      );
    }
  };

  const clearCheckedItems = () => {
    setCheckedItems([]);
  };
  const getListRows = () => {
    return loadedItems.map((item: any, index: number) => {
      return (
        <>
          <GenericListItem
            key={item.id || item.thirdPartyID || item.externalSessionID}
            item={item}
            index={index}
            type={type}
            onSelect={handleItemSelect}
            onDelete={onDeleteItem && handleDeleteItem}
            onEdit={onEditItem && handleEditItem}
            onDuplicate={onDuplicateItem && handleDuplicateItem}
            setDefault={setDefault && handleSetDefault}
            isSelected={
              (item.id && item.id === selectedItem?.id) || // for attendees
              (item.thirdPartyID &&
                item.thirdPartyID === selectedItem?.thirdPartyID) || // for imported attendees
              (item.externalSessionID &&
                item.externalSessionID === selectedItem?.externalSessionID) || // for imported sessions
              (item.attendeeThirdPartyID &&
                item.attendeeThirdPartyID ===
                  selectedItem?.attendeeThirdPartyID) // for imported reservations
            }
            onItemCheck={bulkActions ? handleItemCheck : undefined}
            isChecked={
              checkedItems.findIndex(
                (checkedItem) =>
                  checkedItem ===
                  (type === dataType.RESERVATIONS ? item.attendeeId : item.id)
              ) !== -1
            }
          />
        </>
      );
    });
  };

  // check if a bulk action button should be disabled or not
  const isActiveBulkAction = (action: any) => {
    // disable bulk action button when there is no selection
    if (checkedItems.length === 0) {
      return false;
    }

    // for attendees we must enable the checkout button only when there is at least one attendee selected with the ATTENDED status
    if (
      action.key === "checkout" &&
      type === dataType.ATTENDEES &&
      items
        .filter(
          (item) =>
            checkedItems.findIndex((checkedItem) => checkedItem === item.id) !==
            -1
        )
        .filter(
          (item) => item.registrationStatus === registrationStatus.ATTENDED
        ).length === 0
    ) {
      return false;
    }

    // enable bulk action when selecting at least one item
    return true;
  };

  const listRows = getListRows();
  return (
    <>
      <Box
        sx={[
          classes.root,
          {
            marginTop:
              (bulkActions || customActions) && items.length > 0 && "60px",
            height:
              (bulkActions || customActions) && items.length > 0
                ? "calc(100% - 60px)"
                : "100%",
          },
        ]}
        // className={` ${
        //   (bulkActions || customActions) &&
        //   items.length > 0 &&
        //   classes.hasBulkActionsBar
        // }`}
        style={style}
        className={className}
      >
        {(bulkActions || customActions) && items.length > 0 && (
          <Box sx={classes.bulkActions}>
            {/* left bulk action buttons */}
            <Box flexGrow={1} textAlign="left">
              {bulkActions &&
                bulkActions.map((action: any) => (
                  <FdButton
                    key={action.key}
                    sx={[
                      classes.bulkActionButton,
                      isActiveBulkAction(action)
                        ? { color: theme.palette.primary.main }
                        : {},
                    ]}
                    startIcon={action.startIcon}
                    onClick={() => {
                      if (action.key === "delete") {
                        showWarningDialog(
                          "Are you sure you want to delete the selected items?",
                          () => {
                            action.callback(checkedItems);
                          }
                        );
                      } else if (action.key === "checkout") {
                        showWarningDialog(
                          "Are you sure you want to check out the selected items? This action cannot be undone!",
                          () => {
                            action.callback(checkedItems);
                            clearCheckedItems();
                          },
                          // <FiLogOut size="45" />
                        );
                      } else {
                        action.callback(checkedItems);
                      }
                    }}
                    variant="text"
                    disabled={
                      !isActiveBulkAction(action)
                    }
                    ButtonName={action.label}
                  />
                ))}
            </Box>
            {/* right custom action buttons */}
            <Box>
              {customActions &&
                customActions.map((action: any) => (
                  <Button
                    key={action.key}
                    // color="primary"
                    sx={[
                      classes.customActionButton,
                      {
                        color: `${theme.palette.primary.main}`,
                        cursor: "pointer",
                        marginRight: 0,
                      },
                    ]}
                    startIcon={action.startIcon}
                    onClick={() => {
                      if (action.key === "delete-all") {
                        showWarningDialog(
                          "Are you sure you want to delete all the items?",
                          action.callback
                        );
                      }

                      if (action.key === "checkout-all") {
                        showWarningDialog(
                          "Are you sure you want to checkout attendees?",
                          action.callback,
                          <FiLogOut size="45" />
                        );
                      }

                      // else {
                      //   action.callback(checkedItems);
                      // }
                    }}
                    variant="text"
                  >
                    {action.label}
                  </Button>
                ))}
            </Box>

            {/** Show the number of selected items */}
            {/* <Typography
                component="span"
                style={{ color: "#fff", fontSize: "18px" }}
                align="left"
              >
                {checkedItems.length} selected{" "}
                {`item${checkedItems.length > 1 ? "s" : ""}`}
              </Typography> */}
          </Box>
        )}

        <Box {...({ ref: scrollRef } as any)} sx={classes.listWrapper}>
          {loadedItems.length > 0 && <List dense={false}>{listRows}</List>}

          {loadedItems.length === 0 && <Box>No data</Box>}

          {isLoading && <CircularProgress sx={classes.loading} />}
        </Box>
      </Box>

      {actionDialog.open && (
        <SimpleActionDialog
          open={actionDialog.open}
          title={actionDialog.title}
          onCancel={actionDialog.onCancel}
          onClose={actionDialog.onClose}
          icon={actionDialog.icon}
          type={actionDialog.type}
          onConfirm={actionDialog.onConfirm}
        />
      )}
    </>
  );
};

export default GenericList;
